/**
 * TerraMatrix Universal Data Platform - TableFieldsEditor
 *
 * Level 3: Field-level editing view
 * Shows all fields for a table with AI-assisted description generation.
 * Users can edit individual field descriptions and configure field properties.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Database,
  Table2,
  Loader2,
  Edit2,
  Check,
  X,
  Sparkles,
  RefreshCw,
  Type,
  Hash,
  Calendar,
  ToggleLeft,
  FileJson,
  List,
  HelpCircle,
  Save,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { dataApi, DataSource } from '@/services/api/dataApi';
import { useToast } from '@/hooks/use-toast';

interface FieldInfo {
  name: string;
  type: string;
  description?: string;
  aiDescription?: string;
  nullable?: boolean;
  indexed?: boolean;
  unique?: boolean;
  sampleValues?: any[];
  occurrenceRate?: number;
  cubeType?: string; // dimension, measure, time
  cubeSql?: string;
}

// Icon mapping for field types
const typeIcons: Record<string, React.ReactNode> = {
  string: <Type className="w-4 h-4" />,
  text: <Type className="w-4 h-4" />,
  number: <Hash className="w-4 h-4" />,
  integer: <Hash className="w-4 h-4" />,
  float: <Hash className="w-4 h-4" />,
  double: <Hash className="w-4 h-4" />,
  date: <Calendar className="w-4 h-4" />,
  datetime: <Calendar className="w-4 h-4" />,
  timestamp: <Calendar className="w-4 h-4" />,
  boolean: <ToggleLeft className="w-4 h-4" />,
  object: <FileJson className="w-4 h-4" />,
  array: <List className="w-4 h-4" />,
  unknown: <HelpCircle className="w-4 h-4" />,
};

export const TableFieldsEditor: React.FC = () => {
  const { sourceId, tableName } = useParams<{ sourceId: string; tableName: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  // State
  const [dataSource, setDataSource] = useState<DataSource | null>(null);
  const [fields, setFields] = useState<FieldInfo[]>([]);
  const [tableDescription, setTableDescription] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editing state
  const [editingFieldName, setEditingFieldName] = useState<string | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editCubeType, setEditCubeType] = useState<string>('dimension');
  const [isGeneratingAI, setIsGeneratingAI] = useState<string | null>(null);
  const [isGeneratingAllAI, setIsGeneratingAllAI] = useState(false);

  // Decode table name from URL
  const decodedTableName = tableName ? decodeURIComponent(tableName) : '';

  // Load data source and fields
  const loadData = useCallback(async () => {
    if (!sourceId || !decodedTableName) return;

    setIsLoading(true);
    setError(null);

    try {
      const source = await dataApi.getSource(sourceId);
      setDataSource(source);

      // Find the table in the schema
      const table = source.schema?.tables?.find(
        (t: any) => t.name === decodedTableName
      );

      if (table) {
        setTableDescription(table.description || '');

        // Convert fields to FieldInfo format
        const fieldInfos: FieldInfo[] = (table.fields || []).map((f: any) => ({
          name: f.name,
          type: f.type?.toLowerCase() || 'unknown',
          description: f.description,
          nullable: f.nullable,
          indexed: f.indexed,
          unique: f.unique,
          sampleValues: f.sampleValues,
          occurrenceRate: f.occurrenceRate,
          cubeType: f.cubeType || inferCubeType(f.name, f.type),
          cubeSql: f.cubeSql,
        }));

        setFields(fieldInfos);
      } else {
        setError(`Table "${decodedTableName}" not found in schema`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load table fields');
    } finally {
      setIsLoading(false);
    }
  }, [sourceId, decodedTableName]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Infer Cube.js type from field name and type
  const inferCubeType = (name: string, type: string): string => {
    const lowerName = name.toLowerCase();
    const lowerType = (type || '').toLowerCase();

    // Time dimensions
    if (
      lowerName.includes('date') ||
      lowerName.includes('time') ||
      lowerName.includes('timestamp') ||
      lowerName.includes('created') ||
      lowerName.includes('updated') ||
      lowerType === 'date' ||
      lowerType === 'datetime' ||
      lowerType === 'timestamp'
    ) {
      return 'time';
    }

    // Measures (numeric values that can be aggregated)
    if (
      lowerName.includes('count') ||
      lowerName.includes('total') ||
      lowerName.includes('sum') ||
      lowerName.includes('amount') ||
      lowerName.includes('price') ||
      lowerName.includes('value') ||
      lowerName.includes('quantity') ||
      lowerName.includes('reading') ||
      lowerName.includes('level') ||
      lowerName.includes('temperature') ||
      lowerName.includes('humidity') ||
      lowerName.includes('moisture')
    ) {
      if (lowerType === 'number' || lowerType === 'integer' || lowerType === 'float' || lowerType === 'double') {
        return 'measure';
      }
    }

    // Default to dimension
    return 'dimension';
  };

  // Get icon for field type
  const getTypeIcon = (type: string): React.ReactNode => {
    const lowerType = type.toLowerCase();
    return typeIcons[lowerType] || typeIcons.unknown;
  };

  // Start editing a field
  const startEditingField = (field: FieldInfo) => {
    setEditingFieldName(field.name);
    setEditDescription(field.description || '');
    setEditCubeType(field.cubeType || 'dimension');
  };

  // Save field changes
  const saveFieldChanges = async () => {
    if (!editingFieldName) return;

    const updatedFields = fields.map((f) =>
      f.name === editingFieldName
        ? { ...f, description: editDescription, cubeType: editCubeType }
        : f
    );
    setFields(updatedFields);
    setEditingFieldName(null);
    setEditDescription('');

    toast({
      title: 'Field Updated',
      description: `Updated "${editingFieldName}" description and type.`,
    });
  };

  // Generate AI description for a single field
  const generateFieldAIDescription = async (fieldName: string) => {
    setIsGeneratingAI(fieldName);

    try {
      const field = fields.find((f) => f.name === fieldName);
      if (!field) return;

      // Call AI endpoint (or use local fallback)
      const response = await fetch('/api/analytics/ai/describe-field', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fieldName,
          fieldType: field.type,
          tableName: decodedTableName,
          sampleValues: field.sampleValues,
          existingDescription: field.description,
        }),
      });

      let description: string;
      if (response.ok) {
        const data = await response.json();
        description = data.description;
      } else {
        // Fallback local generation
        description = generateLocalFieldDescription(field);
      }

      // Update field with AI description
      const updatedFields = fields.map((f) =>
        f.name === fieldName ? { ...f, aiDescription: description } : f
      );
      setFields(updatedFields);

      // Start editing with AI suggestion
      startEditingField({ ...field, description });
      setEditDescription(description);
    } catch {
      // Fallback
      const field = fields.find((f) => f.name === fieldName);
      if (field) {
        const description = generateLocalFieldDescription(field);
        startEditingField(field);
        setEditDescription(description);
      }
    } finally {
      setIsGeneratingAI(null);
    }
  };

  // Generate AI descriptions for all fields
  const generateAllFieldDescriptions = async () => {
    setIsGeneratingAllAI(true);

    try {
      const updatedFields = await Promise.all(
        fields.map(async (field) => {
          if (field.description) return field; // Skip if already has description

          try {
            const response = await fetch('/api/analytics/ai/describe-field', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                fieldName: field.name,
                fieldType: field.type,
                tableName: decodedTableName,
                sampleValues: field.sampleValues,
              }),
            });

            if (response.ok) {
              const data = await response.json();
              return { ...field, description: data.description };
            }
          } catch {
            // Ignore and use fallback
          }

          // Fallback
          return { ...field, description: generateLocalFieldDescription(field) };
        })
      );

      setFields(updatedFields);

      toast({
        title: 'AI Descriptions Generated',
        description: `Generated descriptions for ${fields.filter((f) => !f.description).length} fields.`,
      });
    } catch (err) {
      toast({
        title: 'Generation Failed',
        description: err instanceof Error ? err.message : 'Failed to generate descriptions',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingAllAI(false);
    }
  };

  // Local fallback for field description
  const generateLocalFieldDescription = (field: FieldInfo): string => {
    const name = field.name;
    const type = field.type;
    const lowerName = name.toLowerCase();

    // Common field patterns
    if (lowerName === '_id' || lowerName === 'id') {
      return 'Unique identifier for this record.';
    }
    if (lowerName.includes('created') && lowerName.includes('at')) {
      return 'Timestamp when this record was created.';
    }
    if (lowerName.includes('updated') && lowerName.includes('at')) {
      return 'Timestamp when this record was last updated.';
    }
    if (lowerName.includes('device') && lowerName.includes('id')) {
      return 'Reference to the associated device or sensor.';
    }
    if (lowerName.includes('user') && lowerName.includes('id')) {
      return 'Reference to the user who owns or created this record.';
    }
    if (lowerName.includes('timestamp') || lowerName.includes('time')) {
      return 'Timestamp for this data point or event.';
    }
    if (lowerName.includes('value') || lowerName.includes('reading')) {
      return `${type === 'number' ? 'Numeric' : 'Data'} value from sensor measurement.`;
    }
    if (lowerName.includes('level')) {
      return 'Level measurement value (e.g., water level, fill level).';
    }
    if (lowerName.includes('temperature')) {
      return 'Temperature reading in degrees.';
    }
    if (lowerName.includes('humidity') || lowerName.includes('moisture')) {
      return 'Moisture or humidity percentage reading.';
    }
    if (lowerName.includes('status')) {
      return 'Current status or state of the record.';
    }
    if (lowerName.includes('name')) {
      return 'Human-readable name or label.';
    }
    if (lowerName.includes('description')) {
      return 'Text description or notes.';
    }
    if (lowerName.includes('location') || lowerName.includes('coordinates')) {
      return 'Geographic location or coordinates.';
    }
    if (lowerName.includes('config') || lowerName.includes('settings')) {
      return 'Configuration or settings object.';
    }

    // Generic fallback
    return `${type.charAt(0).toUpperCase() + type.slice(1)} field for ${name.replace(/([A-Z])/g, ' $1').toLowerCase().trim()}.`;
  };

  // Save all fields to the server
  const saveAllFields = async () => {
    if (!sourceId || !dataSource) return;

    setIsSaving(true);

    try {
      // Update the table in the schema
      const updatedTables = (dataSource.schema?.tables || []).map((t: any) => {
        if (t.name === decodedTableName) {
          return {
            ...t,
            description: tableDescription,
            fields: fields.map((f) => ({
              name: f.name,
              type: f.type,
              description: f.description,
              nullable: f.nullable,
              indexed: f.indexed,
              unique: f.unique,
              cubeType: f.cubeType,
              cubeSql: f.cubeSql,
            })),
          };
        }
        return t;
      });

      await dataApi.updateSource(sourceId, {
        schema: { tables: updatedTables },
      });

      toast({
        title: 'Schema Saved',
        description: `Saved ${fields.length} field definitions for "${decodedTableName}".`,
      });
    } catch (err) {
      toast({
        title: 'Save Failed',
        description: err instanceof Error ? err.message : 'Failed to save schema',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Get badge color for cube type
  const getCubeTypeBadge = (cubeType: string) => {
    switch (cubeType) {
      case 'measure':
        return <Badge className="bg-blue-500">Measure</Badge>;
      case 'time':
        return <Badge className="bg-purple-500">Time</Badge>;
      default:
        return <Badge variant="outline">Dimension</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !dataSource) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto py-8 px-4">
          <Button
            variant="ghost"
            onClick={() => navigate(`/data/sources/${sourceId}`)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tables
          </Button>
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">{error || 'Table not found'}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(`/data/sources/${sourceId}`)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tables
          </Button>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <Database className="w-4 h-4" />
                <span>{dataSource.name}</span>
                <span>/</span>
                <Table2 className="w-4 h-4" />
              </div>
              <h1 className="text-2xl font-bold">{decodedTableName}</h1>
              <p className="text-muted-foreground">
                {fields.length} fields • Configure field descriptions for AI queries
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={generateAllFieldDescriptions}
                disabled={isGeneratingAllAI}
              >
                {isGeneratingAllAI ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                AI Describe All
              </Button>
              <Button onClick={saveAllFields} disabled={isSaving}>
                {isSaving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Schema
              </Button>
            </div>
          </div>
        </div>

        {/* Table Description */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Table Description</CardTitle>
            <CardDescription>
              Describe what this table contains and how it relates to other data.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={tableDescription}
              onChange={(e) => setTableDescription(e.target.value)}
              placeholder="Describe this table..."
              rows={2}
            />
          </CardContent>
        </Card>

        {/* Fields List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Field Definitions</CardTitle>
            <CardDescription>
              Configure each field with descriptions for AI-powered query generation.
              Set the Cube.js type (Dimension, Measure, Time) for proper query handling.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {fields.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  No fields discovered. Refresh the schema to discover fields.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {fields.map((field) => (
                  <div
                    key={field.name}
                    className="border rounded-lg p-4 hover:border-primary/50 transition-colors"
                  >
                    {editingFieldName === field.name ? (
                      /* Editing Mode */
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          {getTypeIcon(field.type)}
                          <h3 className="font-medium">{field.name}</h3>
                          <Badge variant="secondary">{field.type}</Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <Label>Description</Label>
                            <Textarea
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              placeholder="Describe this field..."
                              rows={2}
                              className="mt-1"
                            />
                          </div>

                          <div>
                            <Label>Cube.js Type</Label>
                            <Select value={editCubeType} onValueChange={setEditCubeType}>
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="dimension">
                                  Dimension (categorical, groupable)
                                </SelectItem>
                                <SelectItem value="measure">
                                  Measure (numeric, aggregatable)
                                </SelectItem>
                                <SelectItem value="time">
                                  Time (date/time dimension)
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveFieldChanges}>
                            <Check className="w-3 h-3 mr-1" />
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingFieldName(null);
                              setEditDescription('');
                            }}
                          >
                            <X className="w-3 h-3 mr-1" />
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* Display Mode */
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {getTypeIcon(field.type)}
                            <h3 className="font-medium">{field.name}</h3>
                            <Badge variant="secondary">{field.type}</Badge>
                            {getCubeTypeBadge(field.cubeType || 'dimension')}
                            {field.nullable === false && (
                              <Badge variant="outline" className="text-xs">
                                Required
                              </Badge>
                            )}
                            {field.indexed && (
                              <Badge variant="outline" className="text-xs">
                                Indexed
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {field.description || (
                              <span className="italic">No description</span>
                            )}
                          </p>
                          {field.sampleValues && field.sampleValues.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Samples:{' '}
                              {field.sampleValues.slice(0, 3).map((v, i) => (
                                <code key={i} className="bg-muted px-1 mx-0.5 rounded">
                                  {String(v).substring(0, 20)}
                                  {String(v).length > 20 ? '...' : ''}
                                </code>
                              ))}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-1 ml-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => generateFieldAIDescription(field.name)}
                            disabled={isGeneratingAI === field.name}
                            title="Generate AI description"
                          >
                            {isGeneratingAI === field.name ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Sparkles className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEditingField(field)}
                            title="Edit field"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TableFieldsEditor;
